RivalIQ.apps.Dashboard.module('views', function (views, App, Backbone, Marionette, $, _) {

    'use strict';

    const PanelTitle = App.views.PanelTitle = Marionette.ItemView.mixin({
        template: '#dashboardWidgetPanelTitleTemplate',

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        tagName: 'h3',
        className: 'panel-title',

        ui: {
            favorite: '.x-favorite',
            info: '.fa-info-circle',
            alert: '.fa-warning'
        },

        dashboardModelEvents: {
            'change': 'render',
            'favorited': 'render',
            'unfavorited': 'render'
        },

        serializeData: function () {
            const me = this;

            return {
                title: me.dashboardModel.getTitle(),
                subtitle: me.dashboardModel.getSubtitle(),
                description: _.result(me.dashboardModel, 'description'),
                alert: _.result(me.dashboardModel, 'alert')
            };
        },

        templateHelpers: function () {
            return RivalIQ.utils.emoji.getTemplateHelpers();
        },

        onRender: function () {
            const me = this;
            me.ui.info.popover({
                trigger: 'hover',
                container: 'body',
                placement: 'auto right',
                html: true
            });

            if (me.ui.alert) {
                me.ui.alert.popover({
                    trigger: 'hover',
                    container: 'body',
                    placement: 'auto right',
                    html: true
                });
            }
        }
    });

    App.views.PanelFooter = RivalIQ.views.LayoutView.mixin({
        template: '#dashboardWidgetPanelFooterTemplate',
        className: 'panel-footer x-export-hide',

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        triggers: {
            'click .x-view-more': 'view:more'
        },

        inEditor: function () {
            const me = this;
            const curation = me.dashboardModel.curation;
            return curation.id === RivalIQ.enums.curation.DASHBOARDS_BUILDER ||
                curation.id === RivalIQ.enums.curation.DASHBOARDS_WIDGET_EDIT;
        },

        templateHelpers: function () {
            const me = this;

            return {
                viewMoreName: me.dashboardModel.getViewMoreName(),
                hasViewMore: me.hasViewMore(),
                isExporting: me.dashboardModel.isExporting()
            };
        },

        hasViewMore: function () {
            const me = this;
            return me.dashboardModel.hasViewMore() && !me.inEditor();
        },

        onViewMore: function () {
            const me = this;
            RivalIQ.execute('history:navigate', _.result(me.dashboardModel, 'viewMorePath'), {
                trigger: true
            });
        }
    });

    App.views.LoadingPanelFooter = App.views.PanelFooter.extend({

        dashboardModelEvents: {
            'change': 'reload'
        },

        // This is tricksy. Normally, render would add itself as a childview to
        // the loading view. However, in this case I don't want to add the footer to
        // the loading view as the loading view is the curation loading view, not the
        // panel loading view. I don't want to gate showing the curation view on this
        // view's load data completing.
        render: function () {
            const me = this;

            me.isRendering = true;

            if (!me.isDestroyed && (!me.isReloading || !me.refresh)) {
                _.result(me, 'onBeforeRenderLoaded');
            }

            me._deferred
                .done(_.bind(me.loadedRender, me))
                .fail(_.bind(me.onLoadDataError, me));

            if (!me.isDataLoaded()) {
                me._load();
            }

            return me;
        },

        onBeforeRenderLoaded: function () {
            const me = this;
            me.$el.addClass('display-none');
        }
    });

    App.views.RowCountPanelFooter = App.views.LoadingPanelFooter.extend({

        // Subclasses should:
        // 1. Provide a template with rowCount, showingCount, and totalCount dom elements.
        // 2. Implement a `loadData` method that populates showingCount and totalCount and, optionally, sets tooMuchData.

        ui: {
            rowCount: '.row-count',
            showingCount: '.x-showing-count',
            totalCount: '.x-total-count'
        },

        onRenderLoaded: function () {
            const me = this;

            const showRowCountSummary = me.shouldShowRowCountSummary();

            if (showRowCountSummary) {
                me.ui.showingCount.text(me.showingCount);
                me.ui.totalCount.text(me.totalCount);
            }

            me.ui.rowCount.toggleClass('display-none', !showRowCountSummary);
            me.$el.toggleClass('display-none', !(showRowCountSummary || me.dashboardModel.hasViewMore()));

            if (me.ui.infoBubbles) {
                me.ui.infoBubbles.popover();
            }
        },

        shouldShowRowCountSummary: function () {
            const me = this;

            const limit = me.getProp('limit');
            return (limit !== -1 && !me.tooMuchData);
        }
    });

    App.views.IncompleteDataPanelFooter = App.views.LoadingPanelFooter.extend({

        template: '#incompleteDataPanelFooterTemplate',

        className: 'panel-footer incomplete-data-footer',

        ui: {
            incompleteDataContainer: '.warning-container',
            dataNotAvailableContainer: '.data-not-available-container',
            dataInfoContainer: '.data-info-container'
        },

        onRenderLoaded: function () {
            const me = this;

            const showIncompleteDataWarning = me.showIncompleteDataWarning();

            me.ui.incompleteDataContainer.toggleClass('display-none', !showIncompleteDataWarning);

            const dataInfoHtml = me.getDataInfoHtml();
            me.ui.dataInfoContainer.toggleClass('display-none', !dataInfoHtml);
            if (dataInfoHtml) {
                me.ui.dataInfoContainer.html(dataInfoHtml);
            }

            const shouldBeVisible = showIncompleteDataWarning || dataInfoHtml || me.hasViewMore();
            me.$el.toggleClass('display-none', !shouldBeVisible);
        },

        // Subclasses should override these methods.
        showIncompleteDataWarning: function () {
            return true;
        },

        getDataInfoHtml: function () {
            return null;
        }
    });

    App.views.WarningLegendFooterView = RivalIQ.apps.Dashboard.views.IncompleteDataPanelFooter.extend({
        loadData: function () {
            const me = this;

            let collection = me.dashboardModel.metricsCollection;
            if (!collection) {
                collection = new RivalIQ.collections.MetricDefinitions(me.dashboardModel.metrics);
            }

            return collection.fetch()
                .then(results => {

                    const hasPartialDataWarning = results.any(metric => {
                        if (metric.get('timeAnalysisMode') === RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD) {
                            const calculationMetadata = metric.getCalculationMetadata();

                            return _.some(calculationMetadata, (metadata) => {
                                return !_.isEmpty(_.get(metadata, ['missingSources'])) && !_.get(calculationMetadata, ['missingAllSources']);
                            });
                        }
                    });

                    me.showIncompleteDataWarning = _.constant(hasPartialDataWarning);

                    const multiHandlePrivateCompanyCounts = results.reduce((memo, metric) => {
                        const source = metric.getSource();
                        if (RivalIQ.enums.sourceDetails[source].privateData) {
                            if (metric.get('benchmarkCompaniesFilter')) {
                                const calculationMetadata = metric.getCalculationMetadata();
                                const period2NumCompanies = _.get(calculationMetadata, ['period2NumCompanies']);
                                if (period2NumCompanies) {
                                    memo[source] = _.max([memo[source], period2NumCompanies]);
                                }
                            } else if (!metric.get('companyId')) {
                                const value = metric.getValue({
                                    format: false
                                });
                                const companyIds = _.map(value, _.first);
                                memo[source] = _.max([memo[source], companyIds.length]);
                            }
                        }
                        return memo;
                    }, {});

                    me.multiHandlePrivateCompanyCounts = multiHandlePrivateCompanyCounts;
                });
        },

        getDataInfoHtml: function () {
            const me = this;

            if (!_.isEmpty(me.multiHandlePrivateCompanyCounts)) {

                const numCompanies = RivalIQ.state.landscape.getCompaniesByCompanySet(me.getProp('companySetDescriptor')).length;

                let message;
                if (_.size(me.multiHandlePrivateCompanyCounts) === 1) {
                    // only one private channel is reflected in these metrics
                    const companiesWithPrivateData = _.values(me.multiHandlePrivateCompanyCounts)[0];
                    if (companiesWithPrivateData !== numCompanies) {
                        message = `Private data available for ${companiesWithPrivateData} of ${numCompanies} companies.`;
                    }
                } else {
                    const hasPartialData = _.some(me.multiHandlePrivateCompanyCounts, companiesWithPrivateData => companiesWithPrivateData !== numCompanies);
                    if (hasPartialData) {
                        message = `Private data isn't available for all handles.`;
                    }
                }

                if (message) {
                    return [
                        $.tagHtml('span', {
                            class: ['fa', 'fa-lock']
                        }),
                        $.tagHtml('span', {
                            text: message
                        })
                    ].join('&nbsp;');
                }
            }
        }
    });

    const Panel = App.views.Panel = RivalIQ.views.LayoutView.mixin({
        template: '#dashboardWidgetPanelTemplate',
        className: function () {
            const me = this;
            return _.compact(['panel panel-default', me.dashboardModel.panelClassName]).join(' ');
        },
        panelTitleView: PanelTitle,

        mixins: {
            props: Marionette.Dashboard.mixins.Props
        },

        regions: {
            title: '.title-region',
            body: {
                selector: '.panel-content-region'
            },
            footer: '.panel-footer-region',
            actions: {
                selector: '.x-actions-region',
                regionClass: RivalIQ.regions.ListRegion
            }
        },

        dashboardModelEvents: {
            'deprecatedMetricCards:reset': 'showBody',
            'change': 'onDashboardModelChange'
        },

        onRender: function () {
            const me = this;
            if (!me._isShown) {

                me.title.show(new me.panelTitleView({
                    dashboardModel: me.dashboardModel
                }));

                me.actions.reset();
                me.showActions();
                me.showContent();
            }
        },

        onDashboardModelChange: function () {
            const me = this;
            me.showWall();
        },

        onLayoutChange: function () {
            const me = this;
            me.showFooter();
        },

        getFooterView: function () {
            const me = this;
            return me.dashboardModel.getFooterView();
        },

        hasFooter: function () {
            const me = this;
            return me.dashboardModel.hasFooter();
        },

        showContent: function () {
            const me = this;

            if (me.hasFooter()) {
                me.showFooter();
            }

            me.showBody();
        },

        showFooter: function () {
            const me = this;
            const Footer = me.getFooterView();
            me.footer.show(new Footer({
                dashboardModel: me.dashboardModel
            }));
        },

        showActions: function () {
            const me = this;
            me.showExportDropdown();
        },

        inEditor: function () {
            const me = this;
            const curation = me.dashboardModel.curation;
            return curation.id === RivalIQ.enums.curation.DASHBOARDS_BUILDER ||
                curation.id === RivalIQ.enums.curation.DASHBOARDS_WIDGET_EDIT;
        },

        isReadOnly: function () {
            return RivalIQ.state.user.isReadOnly();
        },

        getEditActionClassName: function () {
            return 'btn btn-xs btn-default x-export-hide x-edit-action';
        },

        getEditActionRenderState: function () {
            const me = this;
            return {
                className: me.getEditActionClassName()
            };
        },

        getEditActionView: function () {
            const me = this;
            const editActionRenderState = me.getEditActionRenderState();
            return RivalIQ.views.Link.extend({
                action: _.partial(editHandler, me.dashboardModel),
                className: _.get(editActionRenderState, ['className']),
                linkName: [
                    $.tagHtml('span', {
                        text: 'Edit'
                    })
                ].join(''),
                onShow: function () {
                    const me = this;
                    const popover = _.get(editActionRenderState, ['popover']);
                    if (popover) {
                        me.$el.parent().popover(popover);
                    }
                }
            });
        },

        showEditActions: function () {
            const me = this;

            if (me.inEditor() || me.isReadOnly()) {
                return;
            }

            const EditLink = me.getEditActionView();
            me.actions.show(new EditLink());

            me.actions.show(new RivalIQ.views.Link({
                action: _.partial(deleteConfirmation, me.dashboardModel),
                linkName: [
                    $.tagHtml('span', {
                        class: 'fa fa-trash'
                    })
                ].join(''),
                className: 'btn btn-xs btn-default x-export-hide'
            }), {
                preventDestroy: true
            });
        },

        showExportDropdown: function () {
            const me = this;

            if (me.inEditor() || me.isReadOnly()) {
                return;
            }

            if (me.dashboardModel.shouldShowExport()) {
                const view = me.dashboardModel.getExportDropdown({
                    btnClass: 'btn-xs'
                });

                me.actions.show(view, {
                    preventDestroy: true
                });
            }
        },

        showChannelWall: function (channel) {
            const me = this;

            const View = me.dashboardModel.getChannelWallView(channel);

            me.body.show(new View({
                dashboardModel: me.dashboardModel
            }));

            // If props change we might no longer need to show the channel wall
            me.listenToOnce(me.dashboardModel, 'change', me.showContent);
        },

        showPlanWall: function () {
            const me = this;

            const View = me.dashboardModel.getPlanWallView();
            me.body.show(new View({
                dashboardModel: me.dashboardModel
            }));

            // If props change we might no longer need to show the plan wall
            me.listenToOnce(me.dashboardModel, 'change', me.showContent);
        },

        showWall: function () {
            const me = this;

            if (me.dashboardModel.shouldShowPlanWall()) {
                me.showPlanWall();
                return true;
            }
        },

        showBody: function () {
            const me = this;

            if (me.showWall()) {
                return;
            }

            const View = me.dashboardModel.getView();

            me.body.show(new View({
                dashboardModel: me.dashboardModel
            }), {
                forceLoadingView: true
            });
        }
    });

    App.views.DetailsPanel = Panel.extend({
        showExportDropdown: _.noop
    });

    // Panel view with no header or footer.
    App.views.ChromelessPanel = Panel.extend({
        template: '#dashboardWidgetPanelNoChromeTemplate',
        onRender: function () {
            const me = this;
            if (!me._isShown) {
                me.showBody();
            }
        }
    });

    function editHandler(dashboardModel) {
        const url = [
            '/dashboards',
            dashboardModel.get('configurationId'),
            'edit',
            dashboardModel.instanceId
        ].join('/');

        RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.FavoritesChartEdit, {
            chart: dashboardModel.toJSON()
        });
        RivalIQ.execute('history:navigate', url, {
            trigger: true
        });
    }

    function deleteConfirmation(dashboardModel) {
        RivalIQ.execute('show:modal', {
            contentText: 'Are you sure you want to delete this chart?',
            actions: [
                {
                    html: 'Cancel',
                    className: 'btn btn-default',
                    attributes: {
                        type: 'button',
                        'data-dismiss': 'modal'
                    }
                },
                {
                    html: 'Delete',
                    className: 'btn btn-danger',
                    events: {
                        click: deleteHandler(dashboardModel)
                    },
                    attributes: {
                        type: 'button',
                        'data-dismiss': 'modal'
                    }
                }
            ]
        });
    }

    function deleteHandler(dashboardModel) {
        return function () {
            const me = this;
            me.$el.button('loading');

            dashboardModel.curation.remove(dashboardModel)
                .fail(function () {
                    RivalIQ.execute('alert:warning', 'An error occurred deleting this graph, please try again');
                    me.$el.button('reset');
                })
                .done(function () {
                    RivalIQ.execute('tracking:push:auth', RivalIQ.tracking.metric.FavoritesChartDelete, {
                        chart: dashboardModel.toJSON()
                    });

                    if (dashboardModel.isFocus()) {
                        const url = [
                            '/dashboards',
                            dashboardModel.get('configurationId')
                        ].join('/');
                        RivalIQ.execute('history:navigate', url, {
                            trigger: true
                        });
                    }
                });
        };
    }
});
